import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Swords, 
  ArrowLeft, 
  Trophy, 
  Users,
  Flame,
  ThumbsUp,
  Laugh,
  MessageCircle,
  Clock,
  Share2,
  Home
} from 'lucide-react';
import { formatDate } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DebateReplay = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  
  const [replayData, setReplayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReplayData();
  }, [roomId]);

  const fetchReplayData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rooms/${roomId}/all-messages`);
      setReplayData(response.data);
    } catch (error) {
      console.error('Error fetching replay data:', error);
      setError('Failed to load debate replay');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: `Debate: ${replayData?.room?.topic}`,
        text: `Watch this debate on POLIVOX`,
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !replayData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white mb-4">{error || 'Debate not found'}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-white/20 text-white">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const { debate_messages, audience_comments, room } = replayData;
  const messagesA = debate_messages.filter(m => m.side === 'A');
  const messagesB = debate_messages.filter(m => m.side === 'B');
  
  // Interleave messages for timeline view
  const allMessages = [...debate_messages].sort((a, b) => 
    new Date(a.created_at) - new Date(b.created_at)
  );

  const totalVotes = (room.vote_a_count || 0) + (room.vote_b_count || 0);
  const voteAPercent = totalVotes > 0 ? Math.round((room.vote_a_count / totalVotes) * 100) : 50;
  const voteBPercent = totalVotes > 0 ? Math.round((room.vote_b_count / totalVotes) * 100) : 50;

  const getWinnerName = () => {
    if (room.winner === 'A') return room.participant_a_name;
    if (room.winner === 'B') return room.participant_b_name;
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-lg border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(-1)}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <Swords className="h-4 w-4 text-orange-500" />
                  <span className="text-xs text-orange-400 font-medium">DEBATE REPLAY</span>
                </div>
                <h1 className="text-white font-bold text-lg line-clamp-1">{room.topic}</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={handleShare}
                size="sm"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Share2 className="h-4 w-4 mr-1" />
                Share
              </Button>
              <Button
                onClick={() => navigate('/')}
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/10"
              >
                <Home className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Topic Image */}
        {room.topic_image_url && (
          <div className="relative h-48 md:h-64 rounded-xl overflow-hidden mb-6">
            <img 
              src={`${API_URL}${room.topic_image_url}`} 
              alt={room.topic}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          </div>
        )}

        {/* Result Summary Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 mb-6">
          {/* Participants */}
          <div className="flex items-center justify-center gap-6 md:gap-12 mb-6">
            {/* Participant A */}
            <div className={`text-center ${room.winner === 'A' ? 'scale-105' : ''}`}>
              <div className="relative inline-block">
                <img 
                  src={room.participant_a_picture || '/logo.png'} 
                  alt={room.participant_a_name}
                  className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-4 ${
                    room.winner === 'A' ? 'border-yellow-500' : 'border-blue-500'
                  }`}
                  onError={(e) => { e.target.src = '/logo.png'; }}
                />
                {room.winner === 'A' && (
                  <Trophy className="absolute -top-2 -right-2 h-6 w-6 text-yellow-500" />
                )}
              </div>
              <p className="font-semibold text-white mt-2">{room.participant_a_name}</p>
              <p className="text-xs text-blue-400">Side A</p>
              <p className="text-lg font-bold text-blue-400 mt-1">{room.vote_a_count || 0} votes</p>
            </div>
            
            {/* VS */}
            <div className="text-center">
              <div className="text-3xl text-gray-500 font-bold">VS</div>
              {room.winner === 'draw' && (
                <span className="text-xs text-gray-400">DRAW</span>
              )}
            </div>
            
            {/* Participant B */}
            <div className={`text-center ${room.winner === 'B' ? 'scale-105' : ''}`}>
              <div className="relative inline-block">
                <img 
                  src={room.participant_b_picture || '/logo.png'} 
                  alt={room.participant_b_name}
                  className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-4 ${
                    room.winner === 'B' ? 'border-yellow-500' : 'border-red-500'
                  }`}
                  onError={(e) => { e.target.src = '/logo.png'; }}
                />
                {room.winner === 'B' && (
                  <Trophy className="absolute -top-2 -right-2 h-6 w-6 text-yellow-500" />
                )}
              </div>
              <p className="font-semibold text-white mt-2">{room.participant_b_name}</p>
              <p className="text-xs text-red-400">Side B</p>
              <p className="text-lg font-bold text-red-400 mt-1">{room.vote_b_count || 0} votes</p>
            </div>
          </div>

          {/* Result */}
          {room.winner && room.winner !== 'draw' && (
            <div className="text-center mb-4">
              <span className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-400 px-4 py-2 rounded-full font-semibold">
                <Trophy className="h-5 w-5" />
                {getWinnerName()} Wins!
              </span>
            </div>
          )}

          {/* Vote Bar */}
          {totalVotes > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-blue-400">{voteAPercent}%</span>
                <span className="text-gray-400 flex items-center gap-1">
                  <Users className="h-4 w-4" /> {totalVotes} total votes
                </span>
                <span className="text-red-400">{voteBPercent}%</span>
              </div>
              <div className="h-3 bg-gray-700 rounded-full overflow-hidden flex">
                <div className="bg-blue-500" style={{ width: `${voteAPercent}%` }} />
                <div className="bg-red-500" style={{ width: `${voteBPercent}%` }} />
              </div>
            </div>
          )}

          {/* Meta */}
          <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {formatDate(room.ended_at || room.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="h-4 w-4" />
              {debate_messages.length} messages
            </span>
          </div>
        </div>

        {/* Debate Timeline */}
        <div className="bg-white/5 backdrop-blur rounded-xl border border-white/10 overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Swords className="h-4 w-4 text-orange-500" />
            <span className="font-semibold text-white">Debate Timeline</span>
            <span className="text-xs text-gray-500">({debate_messages.length} messages)</span>
          </div>

          {/* Split View */}
          <div className="flex min-h-[400px]">
            {/* Side A Messages */}
            <div className="flex-1 border-r border-white/10 p-4 space-y-3 max-h-[600px] overflow-y-auto">
              <div className="text-center mb-4">
                <img 
                  src={room.participant_a_picture || '/logo.png'}
                  alt={room.participant_a_name}
                  className="w-10 h-10 rounded-full border-2 border-blue-500 mx-auto"
                  onError={(e) => { e.target.src = '/logo.png'; }}
                />
                <p className="text-sm font-medium text-blue-400 mt-1">{room.participant_a_name}</p>
              </div>
              
              {messagesA.length > 0 ? (
                messagesA.map((msg) => (
                  <div key={msg.message_id} className="bg-blue-500/20 rounded-lg p-3">
                    <p className="text-white text-sm">{msg.message_text}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 text-sm">No messages</p>
              )}
            </div>
            
            {/* Side B Messages */}
            <div className="flex-1 p-4 space-y-3 max-h-[600px] overflow-y-auto">
              <div className="text-center mb-4">
                <img 
                  src={room.participant_b_picture || '/logo.png'}
                  alt={room.participant_b_name}
                  className="w-10 h-10 rounded-full border-2 border-red-500 mx-auto"
                  onError={(e) => { e.target.src = '/logo.png'; }}
                />
                <p className="text-sm font-medium text-red-400 mt-1">{room.participant_b_name}</p>
              </div>
              
              {messagesB.length > 0 ? (
                messagesB.map((msg) => (
                  <div key={msg.message_id} className="bg-red-500/20 rounded-lg p-3">
                    <p className="text-white text-sm">{msg.message_text}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 text-sm">No messages</p>
              )}
            </div>
          </div>
        </div>

        {/* Audience Comments */}
        {audience_comments && audience_comments.length > 0 && (
          <div className="bg-white/5 backdrop-blur rounded-xl border border-white/10 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-purple-400" />
              <span className="font-semibold text-white">Audience Comments</span>
              <span className="text-xs text-gray-500">({audience_comments.length})</span>
            </div>
            
            <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
              {audience_comments.map((comment) => (
                <div key={comment.comment_id} className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
                  <img
                    src={comment.user_picture || '/logo.png'}
                    alt={comment.user_name}
                    className="w-8 h-8 rounded-full flex-shrink-0"
                    onError={(e) => { e.target.src = '/logo.png'; }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-purple-400">{comment.user_name}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 mt-1">{comment.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Back to Result */}
        <div className="mt-6 text-center">
          <Button
            onClick={() => navigate(`/rooms/${roomId}/result`)}
            variant="outline"
            className="border-orange-500 text-orange-400 hover:bg-orange-500/20"
          >
            View Result Summary
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DebateReplay;
